'use client';

import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LucideIcon, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Drawer, DrawerTrigger, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';

export interface MainTab {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
}

interface UnifiedNavigationProps {
  mainTabs: MainTab[];
  activeMainTab: string;
  subTabs: MainTab[];
  activeSubTab: string;
  onMainTabChange: (tab: MainTab) => void;
  onSubTabChange: (tab: MainTab) => void;
}

const MainNavigation = ({
  tabs,
  activeTab,
  onTabChange,
}: {
  tabs: MainTab[];
  activeTab: string;
  onTabChange: (tab: MainTab) => void;
}) => {
  return (
    <div className="group/maintabs flex flex-shrink-0 items-center gap-0.5">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab)}
            className={cn(
              'flex h-8 items-center gap-1.5 overflow-hidden rounded-md border border-transparent px-3 transition-all duration-300 ease-in-out',
              isActive ? 'bg-primary text-primary-foreground border-primary/50 shadow-md' : '',
            )}
          >
            {Icon && <Icon className={cn('h-3.5 w-3.5 flex-shrink-0 transition-all duration-300')} />}
            <span
              className={cn(
                'max-w-[200px] translate-x-0 whitespace-nowrap text-xs font-semibold opacity-100 transition-all duration-300',
              )}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const MobileNavigation = ({
  tabs,
  activeTab,
  onTabChange,
}: {
  tabs: MainTab[];
  activeTab: string;
  onTabChange: (tab: MainTab) => void;
}) => {
  const [open, setOpen] = useState(false);
  const activeTabData = tabs.find((t) => t.id === activeTab) ?? tabs[0];
  const ActiveIcon = activeTabData?.icon;

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-md border border-primary/50 bg-primary px-3 text-primary-foreground shadow-md transition-all',
          )}
        >
          {ActiveIcon && <ActiveIcon className="h-3.5 w-3.5 flex-shrink-0" />}
          <span className="flex-1 text-left text-xs font-semibold">{activeTabData?.label}</span>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="text-sm">Navigate to</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-0.5 px-4 pb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  onTabChange(tab);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-semibold transition-all',
                  isActive
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:bg-primary/10 hover:text-primary',
                )}
              >
                {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const SubNavigation = ({
  tabs,
  activeTab,
  onTabChange,
}: {
  tabs: MainTab[];
  activeTab: string;
  onTabChange: (tab: MainTab) => void;
}) => {
  const { t } = useTranslation('common');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = useState(false);

  // Check if content overflows and arrows are needed
  useEffect(() => {
    const checkOverflow = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const hasOverflow = container.scrollWidth > container.clientWidth;
      setShowArrows(hasOverflow);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [tabs]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 200;
    const newScrollLeft =
      direction === 'left' ? container.scrollLeft - scrollAmount : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  return (
    <div className="relative flex items-center overflow-hidden">
      {showArrows && (
        <button
          onClick={() => scroll('left')}
          className="text-muted-foreground hover:text-primary hover:bg-primary/10 hover:border-primary/30 bg-background/80 z-10 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border-2 border-transparent backdrop-blur-sm transition-all md:h-8 md:w-8"
          aria-label={t('navigation.scrollLeft')}
        >
          <ChevronLeft className="h-4 w-4 md:h-3.5 md:w-3.5" />
        </button>
      )}
      <div
        ref={scrollContainerRef}
        className="no-scrollbar flex min-w-0 flex-1 items-center gap-1 overflow-x-auto"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab)}
              className={cn(
                'flex h-9 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border-2 px-3 text-sm font-semibold transition-all md:h-8 md:text-xs',
                isActive
                  ? 'bg-primary/15 text-primary border-primary/40 shadow-sm'
                  : 'text-muted-foreground hover:text-primary hover:bg-primary/10 hover:border-primary/30 border-transparent',
              )}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
      {showArrows && (
        <button
          onClick={() => scroll('right')}
          className="text-muted-foreground hover:text-primary hover:bg-primary/10 hover:border-primary/30 bg-background/80 z-10 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border-2 border-transparent backdrop-blur-sm transition-all md:h-8 md:w-8"
          aria-label={t('navigation.scrollRight')}
        >
          <ChevronRight className="h-4 w-4 md:h-3.5 md:w-3.5" />
        </button>
      )}
    </div>
  );
};

const UnifiedNavigation = ({
  mainTabs,
  activeMainTab,
  subTabs,
  activeSubTab,
  onMainTabChange,
  onSubTabChange,
}: UnifiedNavigationProps) => {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  // Only show sub-navigation if there are 2 or more items (single item is redundant)
  const hasSubTabs = subTabs.length > 1;

  if (!hasSubTabs) {
    return null;
  }

  return (
    <div className="bg-card mb-4 flex inline-flex max-w-full flex-col gap-1 rounded-lg border p-1 shadow-sm md:flex-row">
      {isMobile ? (
        <MobileNavigation tabs={subTabs} activeTab={activeSubTab} onTabChange={onSubTabChange} />
      ) : (
        <MainNavigation tabs={subTabs} activeTab={activeSubTab} onTabChange={onSubTabChange} />
      )}
    </div>
  );
};

export default UnifiedNavigation;
